import type { InfiniteData } from "react-query";
import useVenueProductsService from "@app-contexts/dashboard/features/products/data/services/useVenueProductsService";
import { VenueBasedProductSerializer } from "@app-contexts/dashboard/features/products/data/types";
import type { ComboBoxInputItemType } from "@ui-kit/form/ComboBox/ComboBox.props";
import type { SearchServiceConfig } from "@shared/components/application/form/FormField/types";
import type { PaginatorContract } from "@shared/services/api/types";
import { getFlattenedData } from "@shared/utils/data/data-utils";

export default function useSearchProduct({
  productSearchInput,
  enabled = true,
  customParams,
  single = false,
  service: serviceOverride,
}: {
  productSearchInput: string;
  enabled?: boolean;
  customParams?: Record<string, unknown>;
  single?: boolean;
  service?: SearchServiceConfig;
}): {
  items: VenueBasedProductSerializer[] | ComboBoxInputItemType[];
  isFetching: boolean;
  hasNextPage: boolean | undefined;
  fetchNextPage: () => void;
  isFetchingNextPage: boolean;
} {
  const defaultService = useVenueProductsService();
  const { usePaginated } = serviceOverride ?? defaultService;

  const {
    data: productSearchData,
    isFetching,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = usePaginated({
    params: { q: productSearchInput, page_size: 5, ...customParams },
    options: { enabled },
  });

  const flattenedData = productSearchData
    ? getFlattenedData(
        productSearchData as InfiniteData<
          PaginatorContract<VenueBasedProductSerializer>
        >
      )
    : [];

  const singleSelectItems: VenueBasedProductSerializer[] =
    flattenedData as VenueBasedProductSerializer[];

  const multiSelectItems: ComboBoxInputItemType[] =
    productSearchData && productSearchData.pages
      ? (flattenedData as VenueBasedProductSerializer[]).map((item) => ({
          id: item.id,
          name: item.name,
          displayName: item.name,
          description: item.description,
        }))
      : ([] as ComboBoxInputItemType[]);

  return {
    items: single ? singleSelectItems : multiSelectItems,
    isFetching,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  };
}
